import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AuthService } from '@core/auth/auth.service';
import { IframeService } from '@core/services/iframe.service';
import { InsightsAuthService } from '@core/services/insights-auth.service';

@Component({
  selector: 'app-insights-access-banner',
  imports: [MatButtonModule, MatIconModule, MatProgressSpinnerModule],
  template: `
    @if (isVisible()) {
      <div class="insights-access-banner" [class.insights-access-banner--waiting]="authPending()">
        @if (authPending()) {
          <mat-spinner diameter="20"></mat-spinner>
          <p>Waiting to be signed in to Insights...</p>
        } @else {
          <mat-icon>info</mat-icon>
          <p>{{ message() }}</p>
          <button mat-stroked-button type="button" (click)="connectToInsights()">Sign in to Insights</button>
        }
      </div>
    }
  `,
  styles: [
    `
      :host {
        display: block;
      }

      .insights-access-banner {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        max-width: 1200px;
        margin-bottom: 1.5rem;
        padding: 0.75rem 1rem;
        border-left: 4px solid var(--text-orange);
        background-color: var(--background-orange);
        color: var(--text-orange);
      }

      .insights-access-banner p {
        flex: 1;
        margin: 0;
        color: var(--text-140-color);
      }

      .insights-access-banner--waiting {
        border-left-color: var(--warn-text-color);
        background-color: var(--warn-background-color);
        color: var(--warn-text-color);
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InsightsAccessBannerComponent {
  readonly message = input.required<string>();

  private readonly authService = inject(AuthService);
  private readonly iframeService = inject(IframeService);
  private readonly insightsAuth = inject(InsightsAuthService);

  readonly isVisible = computed(
    () => this.iframeService.isEmbedded && this.authService.isInsightsAuthEligible() && !this.authService.hasInsightsAccess()
  );
  readonly authPending = this.insightsAuth.authPending;

  connectToInsights(): void {
    this.insightsAuth.login();
  }
}
